/**
 * Clinic backend identity/authz reads (verified contracts).
 */

import { apiRequest } from '../api/http';

export interface MeResponse {
  uid: string;
  apc: string;
  personas: readonly string[];
}

export type LiveMembershipStatus = 'active' | 'suspended' | (string & {});

export interface ClinicMembership {
  clinicId: string;
  memberSince: string;
  status: LiveMembershipStatus;
  /**
   * ZOY-612: false = the platform has temporarily deactivated the clinic;
   * the membership is retained. Absent on an older backend = active.
   */
  clinicActive?: boolean;
  /** ZOY-608: entry slug for /c/<slug>; null when not set. */
  slug?: string | null;
  /** ZOY-608: the clinic's display name — what the chooser shows. Null when unprovisioned. */
  displayName?: string | null;
}

/** Render-only authorization feed — every action re-enforced server-side. */
export interface MeAuthzResponse {
  uid: string;
  memberships: readonly ClinicMembership[];
  clinicId: string | null;
  membershipStatus: string | null;
  /** ZOY-612: false while the SELECTED clinic is deactivated (permissions then empty); null when none selected. */
  clinicActive?: boolean | null;
  fullClinicAdmin: boolean;
  permissions: readonly string[];
}

/** The backend's own rule: an absent field means "not deactivated" (additive contract). */
export const clinicIsActive = (m: { clinicActive?: boolean | null }): boolean =>
  m.clinicActive !== false;

export function fetchMe(): Promise<MeResponse> {
  return apiRequest<MeResponse>({ base: 'clinic', path: '/v1/me', clinicHeader: false });
}

/**
 * With a selected clinic the snapshot carries that clinic's permissions;
 * without one (several memberships) clinicId is null and permissions empty —
 * which never means "no clinic": read memberships.
 */
export function fetchMeAuthz(clinicId?: string): Promise<MeAuthzResponse> {
  return apiRequest<MeAuthzResponse>({
    base: 'clinic',
    path: '/v1/me/authz',
    clinicHeader: clinicId !== undefined,
    clinicIdOverride: clinicId,
  });
}
